import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import checkout from '../services/checkout';
import GooglePlacesAutocomplete from 'react-google-autocomplete';
import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import { getCountryCode } from '../utils/countryUtils';
import useMediaQuery from '../hooks/useMediaQuery'; // Hook pour la détection des écrans mobiles
import '../styles/payment-mobile.css'; // Import des styles spécifiques pour mobile

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL, 
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const SHIPPING_ZONES = {
  FRANCE: ['FR'],
  EUROPE: [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'DE', 
    'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 
    'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
  ],
  DOM_TOM: ['GF', 'GP', 'MQ', 'YT', 'RE', 'PM', 'BL', 'MF', 'WF', 'PF', 'NC', 'TF']
};

// Hook personnalisé pour la détection des écrans mobiles (à créer dans src/hooks/useMediaQuery.js)
// Ce hook sera utilisé pour détecter si l'écran est en mode mobile

const Payment = () => {
  // Détection des écrans mobiles (max-width: 768px)
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Référence pour le formulaire de paiement par carte
  const paymentFormRef = useRef(null);
  
  // Fonction pour défiler vers le formulaire de paiement
  const scrollToPaymentForm = () => {
    if (paymentFormRef.current) {
      paymentFormRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  // ...
  // Ajout d'un état pour la validation Stripe
  const [cardComplete, setCardComplete] = useState({number: false, expiry: false, cvc: false});

  // Nettoyage automatique du téléphone
  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    // Autorise uniquement les chiffres et le + en début
    let cleaned = raw.replace(/[^\d+]/g, '');
    // Un seul + au début
    if (cleaned.startsWith('+')) {
      cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
    } else {
      cleaned = cleaned.replace(/\+/g, '');
    }
    setFormData(prev => ({ ...prev, phone: cleaned }));
  };

  const location = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  
  // eslint-disable-next-line no-unused-vars
  const { actId, actTitle, shippingFees: initialShippingFees } = location.state || {};
  
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    lastname: '',
    firstname: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipcode: '',
    country: 'France',
    host_name: '',
    birth_date: '',
    birth_place: ''
  });

  // Gestion des membres de famille (UI + envoi bulk)
  // includeFamilyMembers: active l'UI et l'envoi des membres après paiement
  // familyMembers: liste dynamique de membres { firstname, lastname, birth_date, email?, phone? }
  const [includeFamilyMembers, setIncludeFamilyMembers] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([
    // État initial vide
  ]);

  /**
   * Met à jour un champ d'un membre à l'index donné.
   * On nettoie le téléphone comme pour l'utilisateur principal pour cohérence.
   */
  const handleFamilyMemberChange = (index, field, value) => {
    setFamilyMembers(prev => {
      const next = [...prev];
      if (field === 'phone') {
        let cleaned = String(value || '').replace(/[^\d+]/g, '');
        cleaned = cleaned.startsWith('+') ? '+' + cleaned.slice(1).replace(/\+/g, '') : cleaned.replace(/\+/g, '');
        next[index] = { ...next[index], [field]: cleaned };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  /** Ajoute un membre vide au tableau. */
  const addFamilyMember = () => {
    setFamilyMembers(prev => ([...prev, { firstname: '', lastname: '', birth_date: '', email: '', phone: '' }]));
  };

  /** Supprime un membre à l'index donné. */
  const removeFamilyMember = (index) => {
    setFamilyMembers(prev => prev.filter((_, i) => i !== index));
  };

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [acts, setActs] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedAct, setSelectedAct] = useState('');
  const [shippingFees, setShippingFees] = useState(initialShippingFees || [
    { 'France': null },
    { 'Europe': null },
    { 'DOM-TOM': null }
  ]);
  const [selectedShippingFee, setSelectedShippingFee] = useState(null);

  const submitting = useRef(false);
  // Une nouvelle visite de la page après confirmation démarre une nouvelle commande.
  useEffect(() => {
    if (sessionStorage.getItem('cgci.checkout.completed') === 'true') {
      checkout.newOrder();
      sessionStorage.removeItem('cgci.checkout.completed');
    }
  }, []);

  const determineShippingZone = useCallback((place) => {
    if (!place || !place.address_components) return 'France';

    const countryComponent = place.address_components.find(
      component => component.types.includes('country')
    );

    if (!countryComponent) return 'France';

    const countryCode = countryComponent.short_name;

    if (SHIPPING_ZONES.FRANCE.includes(countryCode)) {
      return 'France';
    } else if (SHIPPING_ZONES.EUROPE.includes(countryCode)) {
      return 'Europe';
    } else if (SHIPPING_ZONES.DOM_TOM.includes(countryCode)) {
      return 'DOM-TOM';
    }

    return 'Europe'; // Default to Europe for other countries
  }, []);

  const updateShippingFee = useCallback((place) => {
    if (!place || !selectedAct) return;

    const zone = determineShippingZone(place);
    const fee = shippingFees.find(f => f[zone] !== undefined)?.[zone];
    
    setSelectedShippingFee(fee);
  }, [determineShippingZone, shippingFees, selectedAct]);

  const handlePlaceSelect = useCallback((place) => {
    if (!place) return;

    // Mettre à jour les données du formulaire
    const addressComponents = place.address_components;
    const streetNumber = addressComponents.find(c => c.types.includes('street_number'))?.long_name || '';
    const route = addressComponents.find(c => c.types.includes('route'))?.long_name || '';
    const city = addressComponents.find(c => c.types.includes('locality'))?.long_name || '';
    const zipCode = addressComponents.find(c => c.types.includes('postal_code'))?.long_name || '';
    const country = addressComponents.find(c => c.types.includes('country'))?.long_name || '';

    setFormData(prev => ({
      ...prev,
      address: `${streetNumber} ${route}`,
      city: city,
      zipcode: zipCode,
      country: country
    }));

    // Mettre à jour les frais d'expédition
    updateShippingFee(place);
  }, [updateShippingFee]);

  // Charger les frais d'expédition quand l'acte est sélectionné
  useEffect(() => {
    const fetchShippingFees = async () => {
      if (!selectedAct) return;

      try {
        const { data, error } = await supabase
          .from('consular_acts')
          .select('shipping_fees')
          .eq('id', selectedAct)
          .single();

        if (error) throw error;

        if (data.shipping_fees) {
          setShippingFees(data.shipping_fees);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          //console.error('Erreur lors de la récupération des frais d\'expédition :', error);
        } else {
          //console.error('Erreur lors de la récupération des frais d\'expédition');
        }
        toast.error('Impossible de récupérer les frais d\'expédition');
      }
    };

    fetchShippingFees();
  }, [selectedAct]);

  const renderAddressInput = () => (
    <div className="form-group">
      <label htmlFor="address" className="form-label">Adresse complète</label>
      <GooglePlacesAutocomplete
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        onPlaceSelected={handlePlaceSelect}
        options={{
          types: ['address'],
          componentRestrictions: { country: [
  // France
  'fr',
  // Europe (UE)
  'at','be','bg','hr','cy','cz','dk','ee','fi','de','gr','hu','ie','it','lv','lt','lu','mt','nl','pl','pt','ro','sk','si','es','se',
  // DOM-TOM
  'gf','gp','mq','yt','re','pm','bl','mf','wf','pf','nc','tf'
] }
        }}
        placeholder="Saisissez votre adresse"
        className="form-input w-full"
      />
    </div>
  );

  /*const renderShippingFees = () => (
    <div className="mt-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Frais d&apos;expédition</h3>
      <div className="space-y-2">
        {shippingFees.map((fee, index) => {
          const zone = Object.keys(fee)[0];
          const amount = fee[zone];
          return amount > 0 ? (
            <div 
              key={index} 
              className={`flex justify-between items-center p-2 rounded ${selectedShippingFee === amount ? 'bg-primary/10' : 'bg-gray-100'}`}
            >
              <span>{zone}</span>
              <span className="ml-2">{amount} €</span>
            </div>
          ) : null;
        })}
      </div>
      {selectedShippingFee && (
        <p className="mt-2 text-green-600">
          Frais d&apos;expédition sélectionnés : {selectedShippingFee} €
        </p>
      )}
    </div>
  );*/

  const [currentStep, setCurrentStep] = useState(1);
  
  // Noms des étapes
  const stepNames = [
    "Sélection de l'acte",
    "Informations personnelles",
    "Adresse d'expédition"
  ];
  
  // Nombre total d'étapes déterminé par la longueur du tableau stepNames
  const totalSteps = stepNames.length;

  // Ref pour suivre l'état de création de l'intention de paiement
  // const paymentIntentRef = React.useRef({
  //   isCreating: false,
  //   lastAttemptTime: 0
  // });

  // Créer l'intention de paiement quand les conditions sont réunies
  // Récupérer les services
  const fetchServices = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('id, name, description')
        .order('name');
      
      //console.log('🔍 Services récupérés', { data, error });
      
      if (error) throw error;
      setServices(data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('❌ Erreur lors de la récupération des services :', err);
      } else {
        //console.error('❌ Erreur lors de la récupération des services');
      }
      toast.error('Impossible de charger les services');
    }
  }, []);

  // Récupérer les catégories pour un service donné
  const fetchCategories = useCallback(async (serviceId) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, description, service_id')
        .order('name');
      
      //console.log('🔍 Catégories récupérées', { serviceId, data, error });
      
      if (error) throw error;
      const filteredCategories = data ? data.filter(category => category.service_id === serviceId) : [];
      setCategories(filteredCategories);
      setSelectedCategory('');
      setActs([]);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('❌ Erreur lors de la récupération des catégories :', err);
      } else {
        //console.error('❌ Erreur lors de la récupération des catégories');
      }
      toast.error('Impossible de charger les catégories');
    }
  }, []);

  // Récupérer les actes consulaires pour une catégorie donnée
  const fetchConsularActs = useCallback(async (categoryId) => {
    try {
      const { data, error } = await supabase
        .from('consular_acts')
        .select('id, title, description, processing_fee, shipping_fee, processing_time, category_id')
        .order('title');
      
      //console.log('🔍 Actes consulaires récupérés', { categoryId, data, error });
      
      if (error) throw error;
      const filteredActs = data ? data.filter(act => act.category_id === categoryId) : [];
      setActs(filteredActs);
      setSelectedAct('');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('❌ Erreur lors de la récupération des actes consulaires :', err);
      } else {
        //console.error('❌ Erreur lors de la récupération des actes consulaires');
      }
      toast.error('Impossible de charger les actes consulaires');
    }
  }, []);

  // Nouvelle fonction pour définir le shipping fee et l'acte sélectionné
  const setShippingFeeAndAct = useCallback((fee, actId) => {
    setSelectedShippingFee(fee);
    setSelectedAct(actId);
  }, []);

  // Charger les services au montage du composant
  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Nouvelle fonction pour gérer la sélection automatique des paramètres d'URL
  const handleUrlParameterSelection = useCallback(async () => {
    const searchParams = new URLSearchParams(location.search);
    const serviceId = searchParams.get('ser');
    const categoryId = searchParams.get('cat');
    const actId = searchParams.get('act');

    if (serviceId && serviceId !== selectedService) {
      // Sélectionner automatiquement le service
      const selectedServiceObj = services.find(service => service.id === serviceId);
      if (selectedServiceObj) {
        setSelectedService(serviceId);
        await fetchCategories(serviceId);
      }
    }

    if (categoryId && categoryId !== selectedCategory) {
      // Sélectionner automatiquement la catégorie
      const selectedCategoryObj = categories.find(category => category.id === categoryId);
      if (selectedCategoryObj) {
        setSelectedCategory(categoryId);
        await fetchConsularActs(categoryId);
      }
    }

    if (actId && actId !== selectedAct) {
      // Sélectionner automatiquement l'acte
      const selectedActObj = acts.find(act => act.id === actId);
      if (selectedActObj) {
        setShippingFeeAndAct(selectedActObj.shipping_fee, actId);
      }
    }
  }, [services, categories, acts, fetchCategories, fetchConsularActs, location.search, selectedService, selectedCategory, selectedAct, setShippingFeeAndAct]);

  useEffect(() => {
    // Appeler la sélection automatique une fois que les services sont chargés
    if (services.length > 0) {
      handleUrlParameterSelection();
    }
  }, [services, handleUrlParameterSelection]);

  // Effet pour passer automatiquement à l'étape 2 quand tout est sélectionné
  useEffect(() => {
    // Vérifier si service, catégorie et acte sont sélectionnés
    if (selectedService && selectedCategory && selectedAct) {
      //console.log('🔍 Tous les éléments sont sélectionnés', { 
      //  selectedService, 
      //  selectedCategory, 
      //  selectedAct 
      //});

      // Passer directement à l'étape 2
      setCurrentStep(2);
    }
  }, [selectedService, selectedCategory, selectedAct]);

  // Gestionnaire de changement de service
  const handleServiceChange = useCallback((serviceId) => {
    setSelectedService(serviceId);
    fetchCategories(serviceId);
  }, [fetchCategories]);

  // Gestionnaire de changement de catégorie
  const handleCategoryChange = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    fetchConsularActs(categoryId);
  }, [fetchConsularActs]);

  // Gestionnaire de changement d'acte consulaire
  const handleActChange = useCallback((actId) => {
    const selectedActDetails = actId ? acts.find(act => act.id === actId) : null;
    setShippingFeeAndAct(selectedActDetails?.shipping_fee, actId);
  }, [acts, setShippingFeeAndAct]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting.current || !stripe || !elements) return;
    submitting.current = true;
    setProcessing(true);
    const loading = toast.loading('Traitement du paiement en cours...');
    try {
      if (!formData.birth_date) throw new Error('Veuillez renseigner votre date de naissance.');
      const members = includeFamilyMembers ? familyMembers.map(m => ({
        firstname: (m.firstname || '').trim(), lastname: (m.lastname || '').trim(),
        birth_date: m.birth_date || '', email: (m.email || '').trim() || null,
        phone: (m.phone || '').trim() || null
      })) : [];
      const { data: prepared } = await checkout.prepare({
        profile: formData, members, consular_act_id: selectedAct,
        country_code: getCountryCode(formData.country)
      });
      let result = prepared.completed;
      if (!result) {
        // Un tarif modifié côté catalogue doit être affiché avant toute confirmation.
        if (prepared.amount !== Number(selectedShippingFee)) {
          setSelectedShippingFee(prepared.amount);
          throw new Error('Le tarif de livraison a été actualisé. Vérifiez le montant puis confirmez à nouveau.');
        }
        const retrieved = await stripe.retrievePaymentIntent(prepared.clientSecret);
        if (retrieved.error) throw new Error(retrieved.error.message);
        let intent = retrieved.paymentIntent;
        if (intent.status !== 'succeeded') {
          const confirmed = await stripe.confirmCardPayment(prepared.clientSecret, {
            payment_method: {
              card: elements.getElement(CardNumberElement),
              billing_details: {
                name: `${formData.firstname} ${formData.lastname}`, email: formData.email, phone: formData.phone,
                address: { line1: formData.address, city: formData.city, postal_code: formData.zipcode,
                  country: getCountryCode(formData.country) }
              }
            }
          });
          if (confirmed.error) throw new Error(confirmed.error.message);
          intent = confirmed.paymentIntent;
        }
        if (intent.status !== 'succeeded') throw new Error('Paiement en cours de confirmation. Veuillez patienter puis réessayer.');
        const completed = await checkout.complete(intent.id);
        result = completed.data;
      }
      sessionStorage.setItem('cgci.checkout.completed', 'true');
      toast.success('Paiement confirmé');
      navigate('/confirmation', { state: result });
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Impossible de terminer la commande');
    } finally {
      toast.dismiss(loading);
      submitting.current = false;
      setProcessing(false);
    }
  }, [stripe, elements, formData, selectedAct, selectedShippingFee, includeFamilyMembers, familyMembers, navigate]);

  return (
    <div style={{width: isMobile ? '100%' : '80%', margin: '0 auto'}} className={`${isMobile ? 'px-2' : 'px-4'} py-6`}>
      <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 mb-4 ${isMobile ? 'text-center' : ''}`}>
        Payer les frais d&apos;expédition du passeport ou des actes établis
      </h1>
      <div className={`${isMobile ? 'flex flex-col' : 'grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3'} gap-6`}>
        <div className={`${isMobile ? '' : 'col-span-2'}`}>
          {/* Étapes avant le paiement */}
          <div className="mb-8 bg-white p-6 rounded-lg shadow-sm">
            <div className={`payment-steps-container ${isMobile ? 'mb-8' : 'mb-16'}`}>
              {/* Ligne horizontale de base */}
              <div className="payment-steps-line"></div>
              
              {/* Ligne de progression */}
              <div 
                className="payment-steps-progress"
                style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
              ></div>
              
              {/* Étapes avec cercles */}
              <div className="flex justify-between w-full relative z-10">
                {stepNames.map((step, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(index + 1)}
                      disabled={index + 1 > currentStep}
                      className={`payment-step-circle ${index + 1 === currentStep ? 'payment-step-active' : 
                        index + 1 < currentStep ? 'payment-step-completed' : 'payment-step-inactive'}`}
                      aria-label={`Aller à l'étape ${index + 1}`}
                    >
                      {index + 1}
                    </button>
                    
                    <span 
                      className={`mt-4 ${isMobile ? 'text-xs' : 'text-sm'} text-center font-medium ${isMobile ? '' : 'whitespace-nowrap'} ${index + 1 === currentStep ? 'text-primary font-bold' : 
                        index + 1 < currentStep ? 'text-primary' : 'text-gray-500'}`}
                    >
                      {isMobile && step.length > 10 ? step.split(' ')[0] : step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Étape 1: Sélection de l'acte consulaire */}
            {currentStep === 1 && (
              <div>
                <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-4 border-b pb-2`}>Sélection de l&apos;acte consulaire</h2>
                <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-3'} gap-4`}>
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700">Service</label>
                    <select id="service" value={selectedService} onChange={(e) => handleServiceChange(e.target.value)} className="form-input">
                      <option value="">Sélectionnez un service</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>{service.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">Catégorie</label>
                    <select id="category" value={selectedCategory} onChange={(e) => handleCategoryChange(e.target.value)} disabled={!selectedService} className="form-input">
                      <option value="">Sélectionnez une catégorie</option>
                      {categories.filter(category => category.service_id === selectedService).map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="act" className="block text-sm font-medium text-gray-700">Acte</label>
                    <select id="act" value={selectedAct} onChange={(e) => handleActChange(e.target.value)} disabled={!selectedCategory} className="form-input">
                      <option value="">Sélectionnez un acte</option>
                      {acts.filter(act => act.category_id === selectedCategory).map(act => (
                        <option key={act.id} value={act.id}>{act.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={`mt-6 flex gap-4 ${isMobile ? 'justify-between' : 'justify-end'}`}>
                  <button 
                    type="button" 
                    onClick={() => setCurrentStep(Math.min(currentStep + 1, totalSteps))} 
                    className={`btn-nav btn-nav-next ${isMobile ? 'w-full' : ''}`}
                  >
                    <span>Suivant</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            
            {/* Étape 2: Informations personnelles */}
            {currentStep === 2 && (
              <div>
                <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-4 border-b pb-2`}>Informations personnelles</h2>
                <div className="">
                  <div className={`form-group grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2'} gap-4`}>
                    <div>
                      <label htmlFor="firstname" className="form-label">Prénom</label>
                      <input type="text" id="firstname" name="firstname" value={formData.firstname} onChange={handleChange} className="form-input" />
                    </div>
                    <div>
                      <label htmlFor="lastname" className="form-label">Nom</label>
                      <input type="text" id="lastname" name="lastname" value={formData.lastname} onChange={handleChange} className="form-input" />
                    </div>
                  </div>
                  <div className="form-group grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="form-label">Email</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="form-input" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="form-label">Téléphone</label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handlePhoneChange} className="form-input" />
                    </div>
                  </div>
                  <div className="form-group grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="birth_date" className="form-label">Date de naissance</label>
                      <input
                        type="date"
                        id="birth_date"
                        name="birth_date"
                        value={formData.birth_date}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                  {/* Section Membres de famille */}
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center gap-3">
                      <input
                        id="include-family-members"
                        type="checkbox"
                        className="h-4 w-4 text-primary border-gray-300 rounded"
                        checked={includeFamilyMembers}
                        onChange={(e) => setIncludeFamilyMembers(e.target.checked)}
                      />
                      <label htmlFor="include-family-members" className="text-sm font-medium text-gray-900">
                        Ajouter des membres de famille
                        <span className="text-gray-500 text-xs block"> (Dont les documents doivent être ajoutés à votre expédition)</span>
                      </label>
                    </div>

                    {includeFamilyMembers && (
                      <div className="mt-4 space-y-4">
                        {familyMembers.length === 0 && (
                          <p className="text-sm text-gray-500">Aucun membre ajouté pour le moment.</p>
                        )}

                        {familyMembers.map((member, index) => (
                          <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                            <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2'} gap-4`}>
                              <div>
                                <label className="form-label">Prénom</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={member.firstname || ''}
                                  onChange={(e) => handleFamilyMemberChange(index, 'firstname', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label">Nom</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={member.lastname || ''}
                                  onChange={(e) => handleFamilyMemberChange(index, 'lastname', e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="form-label">Date de naissance</label>
                                <input
                                  type="date"
                                  className="form-input"
                                  value={member.birth_date || ''}
                                  max={new Date().toISOString().slice(0, 10)}
                                  onChange={(e) => handleFamilyMemberChange(index, 'birth_date', e.target.value)}
                                  required
                                />
                              </div>
                            </div>
                            <div className={`mt-4 grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2'} gap-4`}>
                              <div>
                                <label className="form-label">Email (optionnel)</label>
                                <input
                                  type="email"
                                  className="form-input"
                                  value={member.email || ''}
                                  onChange={(e) => handleFamilyMemberChange(index, 'email', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label">Téléphone (optionnel)</label>
                                <input
                                  type="tel"
                                  className="form-input"
                                  value={member.phone || ''}
                                  onChange={(e) => handleFamilyMemberChange(index, 'phone', e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                              <button type="button" className="btn-modern btn-secondary-modern" onClick={() => removeFamilyMember(index)}>
                                Supprimer
                              </button>
                            </div>
                          </div>
                        ))}

                        <div className="flex justify-start">
                          <button type="button" className="btn-modern btn-primary-modern" onClick={addFamilyMember}>
                            + Ajouter un membre
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">Le prénom, le nom et la date de naissance sont requis pour chaque membre.</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex gap-4 justify-between">
                  <button 
                    type="button" 
                    onClick={() => setCurrentStep(Math.max(currentStep - 1, 1))} 
                    className={`btn-nav btn-nav-prev ${isMobile ? 'w-full' : ''}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Précédent</span>
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setCurrentStep(Math.min(currentStep + 1, totalSteps))} 
                    className={`btn-nav btn-nav-next ${isMobile ? 'w-full' : ''}`}
                  >
                    <span>Suivant</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            
            {/* Étape 3: Adresse d'expédition */}
            {currentStep === 3 && (
              <div>
                <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-4 border-b pb-2`}>Adresse d&apos;expédition</h2>
                {renderAddressInput()}
                <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-3'} ${isMobile ? 'gap-4' : 'gap-x-6 gap-y-6 mb-4 gap-4'}`}>
                  <div className="form-group">
                    <label htmlFor="city" className="form-label">Ville</label>
                    <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="zipcode" className="form-label">Code postal</label>
                    <input type="text" id="zipcode" name="zipcode" value={formData.zipcode} onChange={handleChange} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country" className="form-label">Pays</label>
                    <input type="text" id="country" name="country" value={formData.country} onChange={handleChange} className="form-input" />
                  </div>
                </div>
                <div className="form-group mt-4">
                  <label htmlFor="host_name" className="form-label">Nom et prénom de l&apos;hébergeant (si différent)</label>
                  <input type="text" id="host_name" name="host_name" value={formData.host_name} onChange={handleChange} className="form-input" />
                </div>
                <div className={`mt-6 flex gap-4 ${isMobile ? 'justify-between' : 'justify-start'}`}>
                  <button 
                    type="button" 
                    onClick={() => setCurrentStep(Math.max(currentStep - 1, 1))} 
                    className={`btn-nav btn-nav-prev ${isMobile ? 'flex-1 mr-2' : ''}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Précédent</span>
                  </button>
                  {isMobile && (
                    <button 
                      type="button" 
                      onClick={scrollToPaymentForm} 
                      style={{ backgroundColor: '#3b82f6', color: 'white' }}
                      className="btn-nav btn-nav-next flex-1 whitespace-nowrap hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md flex items-center justify-center"
                    >
                      <span>Payer</span>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="ml-2 h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={`${isMobile ? 'mb-4' : 'lg:col-span-1'}`}>
          <div ref={paymentFormRef} className={`${isMobile ? 'mb-4' : 'mb-8'} bg-white ${isMobile ? 'p-4' : 'p-6'} rounded-lg shadow-sm`}>
            <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-4 border-b pb-2`}>Informations de paiement</h2>
            <div className="form-group mb-6 flex items-center justify-between">
              <label className="form-label">À payer</label>
              <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-primary inline-block bg-primary/10 px-4 py-2 rounded-md`}>{selectedShippingFee ? `${selectedShippingFee} €` : '-'}</div>
            </div>
            <div className="form-group max-w-2xl">
              
              <div className="space-y-4">
                <div className="form-group">
                  <label htmlFor="card-number" className="form-label">Numéro de carte</label>
                  <div className="form-input p-0 overflow-hidden">
                    <CardNumberElement 
                      id="card-number"
                      className="p-3"
                      options={{ 
                        style: { 
                          base: { 
                            fontSize: '16px', 
                            color: '#424770', 
                            '::placeholder': { color: '#aab7c4' }
                          }, 
                          invalid: { color: '#9e2146' }
                        },
                        placeholder: '1234 1234 1234 1234'
                      }}
                      onChange={e => setCardComplete(prev => ({...prev, number: e.complete}))}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: '1' }}>
                    <label htmlFor="card-expiry" className="form-label">Date d&apos;expiration</label>
                    <div className="form-input p-0 overflow-hidden">
                      <CardExpiryElement 
                        id="card-expiry"
                        className="p-3"
                        options={{ 
                          style: { 
                            base: { 
                              fontSize: '16px', 
                              color: '#424770', 
                              '::placeholder': { color: '#aab7c4' }
                            }, 
                            invalid: { color: '#9e2146' }
                          }
                        }}
                        onChange={e => setCardComplete(prev => ({...prev, expiry: e.complete}))}
                      />
                    </div>
                  </div>
                  <div style={{ flex: '1' }}>
                    <label htmlFor="card-cvc" className="form-label">CVC</label>
                    <div className="form-input p-0 overflow-hidden">
                      <CardCvcElement 
                        id="card-cvc"
                        className="p-3"
                        options={{ 
                          style: { 
                            base: { 
                              fontSize: '16px', 
                              color: '#424770', 
                              '::placeholder': { color: '#aab7c4' }
                            }, 
                            invalid: { color: '#9e2146' }
                          }
                        }}
                        onChange={e => setCardComplete(prev => ({...prev, cvc: e.complete}))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 text-gray-600" style={{ minWidth: '16px', minHeight: '16px', marginRight: '10px' }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Votre paiement est sécurisé. Vos informations de carte bancaire ne sont pas stockées sur nos serveurs.
              </p>
            </div>
          </div>
          <div className={`bg-gray-50 ${isMobile ? 'p-4' : 'p-6'} rounded-lg flex flex-row gap-4 justify-center`}>
            <button 
              type="submit" 
              disabled={processing || !stripe || !selectedShippingFee || !cardComplete.number || !cardComplete.expiry || !cardComplete.cvc} 
              className={`btn-modern btn-primary-modern flex items-center gap-4 ${(processing || !stripe || !selectedShippingFee || !cardComplete.number || !cardComplete.expiry || !cardComplete.cvc) ? 'opacity-50 cursor-not-allowed' : ''}`}

              onClick={handleSubmit}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="uppercase font-bold">PAYER {selectedShippingFee ? `${selectedShippingFee} €` : ''}</span>
            </button>
            {/* Bouton Annuler masqué */}
            {/* <button type="button" onClick={() => navigate(-1)} className="btn-modern btn-secondary-modern" disabled={processing}>Annuler</button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
